// src/pages/InvoiceDetail.js
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { get, post } from '../utils/api';
import { StatusBadge, Spinner, formatDate, formatDuration, formatCurrency, Btn, Alert } from '../components/Shared';

const STAGE_ICONS = ['','🏭','📦','✅','📫','🚚','✍️','🏠','📋','✅'];
const STAGE_DEFAULT_NAMES = {
  1:'ERP Invoice Raised', 2:'Stock Removal', 3:'Checking & Label Print',
  4:'Ready for Delivery', 5:'Dispatched', 6:'Delivery Confirmation',
  7:'Delivery Person Returned', 8:'Voucher Status Confirmed'
};

// Find another invoice: search panel shown in the empty space to the right of the detail page.
function InvoiceSearchPanel() {
  const navigate = useNavigate();
  const [q, setQ] = useState('');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [rows, setRows] = useState(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const [wide, setWide] = useState(typeof window !== 'undefined' && window.innerWidth >= 1380);
  useEffect(() => {
    const onResize = () => setWide(window.innerWidth >= 1380);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  async function run(e) {
    if (e) e.preventDefault();
    setBusy(true); setErr('');
    try {
      const p = new URLSearchParams();
      if (q.trim()) p.set('invoice_number', q.trim());
      if (from) p.set('invoice_date_from', from);
      if (to) p.set('invoice_date_to', to);
      p.set('limit', '15');
      const data = await get('/api/invoices?' + p.toString());
      setRows(data.invoices || data.rows || []);
    } catch (ex) {
      setErr('Search failed. Try again.');
      setRows(null);
    }
    setBusy(false);
  }

  if (!wide) return null;

  const inp = 'w-full border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 text-sm bg-white dark:bg-gray-800';
  const dshort = (v) => (v ? new Date(v).toLocaleDateString('en-GB', { day: '2-digit', month: 'short' }) : '');

  return (
    <div className="bg-white dark:bg-gray-800 rounded-2xl shadow p-4">
      <h3 className="text-sm font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400 mb-3">Find another invoice</h3>
      <form onSubmit={run} className="space-y-2">
        <input className={inp} placeholder="Invoice number or party name" value={q} onChange={e => setQ(e.target.value)} />
        <div className="grid grid-cols-2 gap-2">
          <div>
            <label className="block text-[11px] text-gray-500 mb-0.5">From</label>
            <input type="date" className={inp} value={from} onChange={e => setFrom(e.target.value)} />
          </div>
          <div>
            <label className="block text-[11px] text-gray-500 mb-0.5">To</label>
            <input type="date" className={inp} value={to} onChange={e => setTo(e.target.value)} />
          </div>
        </div>
        <button type="submit" disabled={busy} className="w-full py-2 rounded-lg bg-brand-700 text-white text-sm font-semibold disabled:opacity-60">
          {busy ? 'Searching...' : 'Search'}
        </button>
      </form>
      {err && <p className="text-xs text-red-600 mt-2">{err}</p>}
      {rows && rows.length === 0 && <p className="text-xs text-gray-500 mt-3">No invoices found.</p>}
      {rows && rows.length > 0 && (
        <div className="mt-3 divide-y divide-gray-100 dark:divide-gray-700 max-h-[60vh] overflow-y-auto">
          {rows.map(r => (
            <button key={r.invoice_id} onClick={() => navigate('/invoices/' + r.invoice_id)}
              className="w-full text-left py-2 px-1 hover:bg-gray-50 dark:hover:bg-gray-700 rounded">
              <div className="flex items-center justify-between gap-2">
                <span className="text-sm font-semibold text-brand-700 dark:text-brand-300">{r.invoice_number}</span>
                <span className="text-xs text-gray-500 tabular-nums">{dshort(r.invoice_date || r.received_at)}</span>
              </div>
              <div className="text-xs text-gray-700 dark:text-gray-300 truncate">{r.party_name}{r.city ? ' (' + r.city + ')' : ''}</div>
              <div className="flex items-center justify-between mt-0.5">
                <StatusBadge status={r.status} />
                <span className="text-xs text-gray-600 dark:text-gray-400 tabular-nums">{formatCurrency(r.invoice_value)}</span>
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

export default function InvoiceDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [invoice,      setInvoice]      = useState(null);
  const [stages,       setStages]       = useState([]);
  const [relatedInvoices, setRelatedInvoices] = useState([]);
  const [loading,      setLoading]      = useState(true);
  const [msg,          setMsg]          = useState(null);
  const [showLabel,    setShowLabel]    = useState(false);
  const [labelData,    setLabelData]    = useState(null);
  const [loadingLabel, setLoadingLabel] = useState(false);
  const [printing,     setPrinting]     = useState(false);

  useEffect(() => {
    async function load() {
      try {
        const data = await get('/api/invoices/' + id);
        setInvoice(data.invoice);
        setStages(data.stages);
        setRelatedInvoices(data.relatedInvoices || []);
      } catch (err) {
        setMsg({ type: 'error', text: err.message });
      } finally {
        setLoading(false);
      }
    }
    load();
  }, [id]);

  async function loadLabel() {
    setLoadingLabel(true);
    try {
      const data = await get('/api/labels/preview/' + id);
      setLabelData(data);
      setShowLabel(true);
    } catch (err) {
      setMsg({ type: 'error', text: 'Could not load label: ' + err.message });
    } finally {
      setLoadingLabel(false);
    }
  }

  async function printLabel() {
    setPrinting(true);
    try {
      const res = await post('/api/labels/print/' + id, {});
      setMsg({ type: res.success ? 'success' : 'error', text: res.success ? 'Label sent to printer!' : res.error });
    } catch (err) {
      setMsg({ type: 'error', text: err.message });
    } finally {
      setPrinting(false);
    }
  }

  async function cancelInvoice() {
    if (!window.confirm('Cancel this invoice?')) return;
    try {
      await post('/api/invoices/' + id + '/cancel', { reason: 'Cancelled by admin' });
      setMsg({ type: 'success', text: 'Invoice cancelled' });
      const data = await get('/api/invoices/' + id);
      setInvoice(data.invoice);
    } catch (err) {
      setMsg({ type: 'error', text: err.message });
    }
  }

  async function markVoucherSigned() {
    try {
      await post('/api/vouchers/' + invoice.voucher_id + '/signed', {});
      setMsg({ type: 'success', text: 'Voucher marked as signed' });
      const data = await get('/api/invoices/' + id);
      setInvoice(data.invoice);
    } catch (err) {
      setMsg({ type: 'error', text: err.message });
    }
  }

  async function markVoucherReturned() {
    try {
      await post('/api/vouchers/' + invoice.voucher_id + '/returned', {});
      setMsg({ type: 'success', text: 'Delivery Boy Returned' });
      const data = await get('/api/invoices/' + id);
      setInvoice(data.invoice);
    } catch (err) {
      setMsg({ type: 'error', text: err.message });
    }
  }

  if (loading) return <div className="flex justify-center p-12"><Spinner size="lg" /></div>;

  if (!invoice) return (
    <div className="p-6">
      <Alert type="error" message="Invoice not found" />
      <button onClick={() => navigate(-1)} className="mt-4 text-brand-500 underline text-sm">Back</button>
    </div>
  );

  return (
    <div className="p-6 max-w-5xl relative">
      <aside className="absolute top-6 w-72" style={{ left: 'calc(100% + 1.5rem)' }}>
        <InvoiceSearchPanel />
      </aside>

      <div className="flex justify-between items-start mb-6">
        <div>
          <button onClick={() => navigate(-1)} className="text-sm text-brand-500 hover:underline mb-2 block">
            Back to Dashboard
          </button>
          <h1 className="text-2xl font-bold text-gray-800 dark:text-gray-100">{invoice.invoice_number}</h1>
          <div className="flex items-center gap-3 mt-1">
            <StatusBadge status={invoice.status} />
            <span className="text-sm text-gray-500 dark:text-gray-400">Received: {formatDate(invoice.received_at)}</span>
          </div>
        </div>
        <div className="flex gap-2 flex-wrap justify-end">
          <Btn variant="outline" size="sm" onClick={loadLabel} disabled={loadingLabel}>
            {loadingLabel ? 'Loading...' : 'View Label'}
          </Btn>
          {invoice.status !== 'cancelled' && invoice.status !== 'complete' && (
            <Btn variant="danger" size="sm" onClick={cancelInvoice}>Cancel Invoice</Btn>
          )}
          {invoice.voucher_id && !invoice.is_signed && (
            <Btn variant="success" size="sm" onClick={markVoucherSigned}>Mark Voucher Signed</Btn>
          )}
          {invoice.voucher_id && invoice.is_signed && !invoice.is_returned_to_office && (
            <Btn variant="primary" size="sm" onClick={markVoucherReturned}>Mark Delivery Boy Returned</Btn>
          )}
        </div>
      </div>

      {msg && <Alert type={msg.type} message={msg.text} onClose={() => setMsg(null)} />}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-1 space-y-4">

          <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-5">
            <h2 className="font-semibold text-gray-700 dark:text-gray-300 mb-3 text-sm uppercase tracking-wide">Delivery Address</h2>
            <p className="font-bold text-gray-800 dark:text-gray-100 text-lg">{invoice.party_name}</p>
            <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">{invoice.address_line1}</p>
            {invoice.address_line2 && <p className="text-sm text-gray-600 dark:text-gray-400">{invoice.address_line2}</p>}
            <p className="text-sm text-gray-600 dark:text-gray-400">{invoice.city}, {invoice.state} - {invoice.pincode}</p>
            <p className="text-sm text-gray-600 dark:text-gray-400 mt-2">{invoice.party_phone}</p>
          </div>

          <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-5">
            <h2 className="font-semibold text-gray-700 dark:text-gray-300 mb-3 text-sm uppercase tracking-wide">Invoice Info</h2>
            <div className="space-y-2 text-sm">
              <Row label="Invoice #" value={invoice.invoice_number} />
              <Row label="ERP Ref"   value={invoice.erp_reference || '-'} />
              <Row label="Value"     value={formatCurrency(invoice.invoice_value)} />
              <Row label="Date"      value={formatDate(invoice.invoice_date)} />
              {invoice.item_summary && <Row label="Summary" value={invoice.item_summary} />}
            </div>
            {invoice.special_instructions && (
              <div className="mt-3 p-2 bg-amber-50 border border-amber-200 rounded text-xs text-amber-700">
                {invoice.special_instructions}
              </div>
            )}
          </div>

          {invoice.delivery_type && (
            <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-5">
              <h2 className="font-semibold text-gray-700 dark:text-gray-300 mb-3 text-sm uppercase tracking-wide">Delivery Type</h2>
              {/* Instation/outstation categorical color-coding — left
                  untouched, same reasoning as invoice stage colors */}
              <span className={'text-sm font-bold px-3 py-1 rounded-full capitalize ' +
                (invoice.delivery_type === 'instation'  ? 'bg-blue-100 text-blue-700' :
                 invoice.delivery_type === 'outstation' ? 'bg-purple-100 text-purple-700' :
                 'bg-amber-100 text-amber-700')}>
                {invoice.delivery_type}
              </span>
              {invoice.classified_by && (
                <p className="text-xs text-gray-400 dark:text-gray-500 mt-2">Classified by: {invoice.classified_by}</p>
              )}
              {invoice.override_reason && (
                <p className="text-xs text-amber-600 mt-1">Override: {invoice.override_reason}</p>
              )}
            </div>
          )}

          {invoice.delivery_id && (
            <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-5">
              <h2 className="font-semibold text-gray-700 dark:text-gray-300 mb-3 text-sm uppercase tracking-wide">Delivery</h2>
              <div className="space-y-2 text-sm">
                <Row label="Person"   value={invoice.delivery_person_name || '-'} />
                <Row label="Started"  value={formatDate(invoice.delivery_started_at)} />
                <Row label="Returned" value={formatDate(invoice.delivery_returned_at)} />
                <Row label="Duration" value={formatDuration(invoice.delivery_duration_seconds)} />
                {invoice.delivery_lat && (
                  <a
                    href={'https://www.google.com/maps?q=' + invoice.delivery_lat + ',' + invoice.delivery_lng}
                    target="_blank"
                    rel="noreferrer"
                    className="text-brand-500 text-xs underline block mt-1"
                  >
                    View on Google Maps
                  </a>
                )}
              </div>
            </div>
          )}

          {invoice.voucher_id && (
            <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-5">
              <h2 className="font-semibold text-gray-700 dark:text-gray-300 mb-3 text-sm uppercase tracking-wide">Voucher</h2>
              <div className="space-y-2 text-sm">
                <Row label="Outcome"  value={invoice.voucher_outcome} />
                <Row label="Voucher"  value={invoice.voucher_phase === 'confirmed'
                  ? 'Confirmed — ' + (invoice.confirmation_type || 'no type').replace(/_/g, ' ')
                  : invoice.voucher_phase === 'awaiting_voucher'
                    ? 'Awaiting voucher' + (invoice.confirmation_type ? ' (' + invoice.confirmation_type.replace(/_/g, ' ') + ')' : '')
                    : 'With driver'} />
              </div>
              {invoice.photo_url && (
                <a href={invoice.photo_url.startsWith('http') ? invoice.photo_url : invoice.photo_url} target="_blank" rel="noreferrer">
                  <img src={invoice.photo_url.startsWith('http') ? invoice.photo_url : invoice.photo_url} alt="Voucher"
                    className="w-full rounded mt-3 border border-gray-200 dark:border-gray-700 hover:opacity-80" />
                </a>
              )}
              {invoice.voice_note_url && (
                <div className="mt-3">
                  <p className="text-xs font-semibold text-gray-500 dark:text-gray-400 mb-1">🎙️ Voice Note</p>
                  <audio
                    controls
                    src={invoice.voice_note_url.startsWith('http') ? invoice.voice_note_url : invoice.voice_note_url}
                    className="w-full h-10"
                  />
                </div>
              )}
            </div>
          )}

          {relatedInvoices.length > 0 && (
            <div className="bg-amber-50 rounded-xl border-2 border-amber-300 p-5">
              <h2 className="font-semibold text-amber-800 mb-1 text-sm uppercase tracking-wide">⚠️ Possible Duplicate Party</h2>
              <p className="text-xs text-amber-700 mb-3">
                Another party record with the exact same name and address exists — likely the same real customer entered twice in Marg.
              </p>
              <div className="space-y-2">
                {relatedInvoices.map(r => (
                  <button key={r.invoice_id} onClick={() => navigate(`/invoices/${r.invoice_id}`)}
                    className="w-full text-left flex items-center justify-between bg-white dark:bg-gray-800 rounded-lg border border-amber-200 px-3 py-2 hover:border-amber-400 transition-colors">
                    <div>
                      <p className="font-mono font-semibold text-amber-900 text-sm">{r.invoice_number}</p>
                      <p className="text-xs text-gray-500 dark:text-gray-400">{r.party_name} · {r.city}</p>
                    </div>
                    <span className="text-xs bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-400 px-2 py-0.5 rounded-full">{r.status?.replace(/_/g, ' ')}</span>
                  </button>
                ))}
              </div>
            </div>
          )}

        </div>

        <div className="lg:col-span-2">
          <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-5">
            <h2 className="font-semibold text-gray-700 dark:text-gray-300 mb-5 text-sm uppercase tracking-wide">Full Audit Trail</h2>
            {/* NOTE: this timeline's done/active/pending colors (green/blue/gray)
                are the core visual status of workflow progress — same protected
                category as STATUS_COLORS in Shared.js. Left untouched. */}
            <div className="relative">
              <div className="absolute left-5 top-0 bottom-0 w-0.5 bg-gray-200" />
              <div className="space-y-4">
                {[1,2,3,4,5,6,7,8].map(stageNum => {
                  const stage  = stages.find(s => s.stage_number === stageNum);
                  const done   = !!stage && !!stage.completed_at;
                  const active = !!stage && !!stage.picked_at && !stage.completed_at;
                  let stage5Duration = null;
                  if (stageNum === 5 && done) {
                    const stage6 = stages.find(s => s.stage_number === 6);
                    const startMs = new Date(stage.completed_at).getTime();
                    const endMs = stage6 && stage6.completed_at ? new Date(stage6.completed_at).getTime() : Date.now();
                    stage5Duration = Math.max(0, Math.round((endMs - startMs) / 1000));
                  }
                  return (
                    <div key={stageNum} className="flex gap-4 relative">
                      <div className={'w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 z-10 text-lg border-2 ' +
                        (done   ? 'bg-green-500 border-green-500 text-white' :
                         active ? 'bg-blue-500 border-blue-500 text-white' :
                                  'bg-white dark:bg-gray-800 border-gray-300 dark:border-gray-600 text-gray-400 dark:text-gray-500')}>
                        {done ? '✓' : active ? '▶' : (STAGE_ICONS[stageNum] || stageNum)}
                      </div>
                      <div className={'flex-1 pb-4 ' + (stageNum === 8 ? '' : 'border-b border-gray-100 dark:border-gray-700')}>
                        <div className="flex justify-between items-start">
                          <div>
                            <p className={'font-semibold text-sm ' +
                              (done ? 'text-gray-800 dark:text-gray-100' : active ? 'text-blue-700' : 'text-gray-400 dark:text-gray-500')}>
                              Stage {stageNum}: {(stage && stage.stage_name) || STAGE_DEFAULT_NAMES[stageNum]}
                            </p>
                            {stage && stage.handler_name && (
                              <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">Handler: {stage.handler_name}</p>
                            )}
                          </div>
                          {done && (
                            <span className="text-xs text-green-600 font-medium bg-green-50 px-2 py-0.5 rounded-full">
                              {stageNum === 5 ? formatDuration(stage5Duration) : formatDuration(stage.duration_seconds)}
                            </span>
                          )}
                          {active && (
                            <span className="text-xs text-blue-600 font-medium bg-blue-50 px-2 py-0.5 rounded-full animate-pulse">
                              In progress
                            </span>
                          )}
                        </div>
                        {stage && (
                          <div className="mt-1 text-xs text-gray-400 dark:text-gray-500 flex gap-4 flex-wrap">
                            {stage.picked_at    && <span>Picked: {formatDate(stage.picked_at)}</span>}
                            {stage.completed_at && <span>Done: {formatDate(stage.completed_at)}</span>}
                            {stage.label_printed && <span className="text-brand-500">Label printed {stage.label_print_count}x</span>}
                            {stage.notes        && <span className="text-amber-600">Note: {stage.notes}</span>}
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
      </div>

      {showLabel && labelData && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl w-full max-w-2xl max-h-screen overflow-y-auto">
            <div className="flex justify-between items-center p-5 border-b sticky top-0 bg-white dark:bg-gray-800 z-10">
              <div>
                <h2 className="text-lg font-bold text-gray-800 dark:text-gray-100">Address Label</h2>
                <p className="text-xs text-gray-500 dark:text-gray-400">{invoice.invoice_number}</p>
              </div>
              <div className="flex gap-2 items-center">
                <a
                  href={`/api/labels/designer/${id}?token=${localStorage.getItem('dms_token')}`}
                  target="_blank"
                  rel="noreferrer"
                  className="px-3 py-1.5 bg-indigo-600 text-white text-xs rounded-lg font-semibold hover:bg-indigo-500"
                >
                  Open Designer
                </a>
                <button onClick={() => setShowLabel(false)} className="text-gray-400 dark:text-gray-500 text-2xl w-8 h-8 flex items-center justify-center">×</button>
              </div>
            </div>
            <div className="p-5">
              <div id="dms-label-print" style={{ border:'2px dashed #ccc', borderRadius:'8px', background:'#fff', fontFamily:'Arial,sans-serif', color:'#000', display:'flex', flexDirection:'column', padding:'22px 26px 18px 26px', width:'580px', height:'409px', boxSizing:'border-box', overflowX:'auto' }}>

                <div style={{ display:'flex', gap:'20px', flex:1, minHeight:0 }}>

                  <div style={{ flex:1, minWidth:0, display:'flex', flexDirection:'column', gap:'9px' }}>
                    <p style={{ fontSize:'30px', fontWeight:'bold', color:'#000', lineHeight:1.15, whiteSpace:'nowrap', overflow:'hidden', textOverflow:'ellipsis' }}>
                      {labelData.labelData?.partyName}
                    </p>
                    {(labelData.labelData?.areaName || labelData.labelData?.station) && (
                      <div style={{ display:'flex', gap:'7px', flexWrap:'wrap' }}>
                        {labelData.labelData?.areaName && (
                          <span style={{ fontSize:'14px', fontWeight:'bold', color:'#0C447C', background:'#E6F1FB', border:'1px solid #85B7EB', borderRadius:'4px', padding:'2px 11px' }}>
                            {labelData.labelData.areaName}
                          </span>
                        )}
                        {labelData.labelData?.station && (
                          <span style={{ fontSize:'14px', fontWeight:'bold', color:'#0C447C', background:'#E6F1FB', border:'1px solid #85B7EB', borderRadius:'4px', padding:'2px 11px' }}>
                            {labelData.labelData.station}
                          </span>
                        )}
                      </div>
                    )}
                    <div style={{ fontSize:'14px', color:'#555', lineHeight:1.6 }}>
                      {labelData.labelData?.addressLine1 && <div>{labelData.labelData.addressLine1}</div>}
                      {labelData.labelData?.addressLine2 && <div>{labelData.labelData.addressLine2}</div>}
                      <div>{labelData.labelData?.city} — {labelData.labelData?.pincode}</div>
                      <div style={{ fontWeight:'bold', color:'#000', fontSize:'15px' }}>{labelData.labelData?.phone}</div>
                    </div>
                    <div style={{ borderTop:'1px solid #ddd', margin:'5px 0' }} />
                    <div style={{ display:'flex', gap:'24px', alignItems:'baseline' }}>
                      <div>
                        <div style={{ fontSize:'11px', color:'#999', textTransform:'uppercase', letterSpacing:'.5px', marginBottom:'2px' }}>Invoice</div>
                        <div style={{ fontSize:'24px', fontWeight:'bold', fontFamily:'monospace', letterSpacing:'1px' }}>{labelData.labelData?.invoiceNumber}</div>
                      </div>
                      <div>
                        <div style={{ fontSize:'11px', color:'#999', textTransform:'uppercase', letterSpacing:'.5px', marginBottom:'2px' }}>Items</div>
                        <div style={{ fontSize:'18px', fontWeight:'bold' }}>{labelData.labelData?.itemCount}</div>
                      </div>
                    </div>
                  </div>

                  <div style={{ display:'flex', flexDirection:'column', alignItems:'center', gap:'8px', flexShrink:0, width:'150px' }}>
                    {labelData.qrBase64 && (
                      <div style={{ display:'flex', flexDirection:'column', alignItems:'center', gap:'4px' }}>
                        <img src={labelData.qrBase64} alt="QR" style={{ width:'135px', height:'135px', border:'1px solid #eee', borderRadius:'4px' }} />
                        <span style={{ fontSize:'11px', color:'#999' }}>QR Code</span>
                      </div>
                    )}
                  </div>

                </div>

                <div style={{ borderTop:'3px solid #1F3A6E', paddingTop:'9px', marginTop:'12px', fontSize:'14px', fontWeight:'bold', color:'#1F3A6E', textTransform:'uppercase', letterSpacing:'2px' }}>
                  {labelData.labelData?.companyName || 'YOUR COMPANY'}
                </div>

                {labelData.barcodeSVGBase64 && (
                  <div style={{ borderTop:'1px solid #eee', paddingTop:'6px', marginTop:'6px', display:'flex', flexDirection:'column', alignItems:'center', gap:'2px' }}>
                    <img src={labelData.barcodeSVGBase64} alt="barcode" style={{ width:'100%', maxHeight:'58px', objectFit:'contain' }} />
                  </div>
                )}
              </div>

              {labelData.qrData && (
                <div className="mt-3 bg-gray-50 dark:bg-gray-900 rounded-xl p-3 border border-gray-200 dark:border-gray-700">
                  <p className="text-xs font-semibold text-gray-600 dark:text-gray-400 mb-1">QR Code Data</p>
                  <p className="text-xs text-gray-500 dark:text-gray-400 font-mono break-all">{labelData.qrData}</p>
                </div>
              )}

              <div className="flex gap-3 mt-4">
                <button
                  onClick={async () => {
                    try {
                      const token = localStorage.getItem('dms_token');
                      const res = await fetch(`/api/labels/designer/${id}?token=${token}`);
                      const html = await res.text();
                      const w = window.open('', '_blank', 'width=800,height=500');
                      w.document.write(html);
                      w.document.close();
                      w.focus();
                      setTimeout(() => { w.print(); }, 800);
                    } catch(e) { alert('Print failed: ' + e.message); }
                  }}
                  className="flex-1 py-3 bg-brand-700 text-white rounded-xl font-semibold text-sm hover:bg-brand-500"
                >
                  🖨️ Print Label
                </button>
                <button
                  onClick={() => setShowLabel(false)}
                  className="px-5 py-3 border border-gray-300 dark:border-gray-600 rounded-xl text-sm text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-800"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

    </div>
  );
}

function Row({ label, value }) {
  return (
    <div className="flex justify-between items-start gap-2">
      <span className="text-gray-400 dark:text-gray-500 text-xs flex-shrink-0">{label}</span>
      <span className="text-gray-700 dark:text-gray-300 text-xs text-right">{value}</span>
    </div>
  );
}
